import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionLabel, Chip } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import CreatorOnboardingRow from './CreatorOnboardingRow';
import CreatorDetailSheet from './CreatorDetailSheet';

/**
 * Module 2 — Bucket of creators sharing a status (Critical / At risk / On track / Passed).
 * Renders rows inside a Card with a colored accent stripe.
 *
 * Props:
 *   - kind: 'critical' | 'at_risk' | 'on_track' | 'passed'
 *   - creators: array of creator_current_month rows
 *   - expandedId: id of currently expanded row (controlled)
 *   - onExpand: (id) => void
 *   - onCreateActionPlan: (creator) => void
 */
const KIND_ACCENT = {
  critical: 'red',
  at_risk: 'amber',
  on_track: 'cyan',
  passed: 'green',
};

const KIND_LABELS = {
  critical: 'Critical',
  at_risk: 'At risk',
  on_track: 'On track',
  passed: 'Passed',
};

const CreatorBucket = ({
  kind,
  creators = [],
  expandedId,
  onExpand,
  onCreateActionPlan,
}) => {
  const { t } = useTranslation();
  const accent = KIND_ACCENT[kind] || 'cyan';

  if (!creators.length) return null;

  return (
    <Card accent={accent} padded={false} style={{ marginBottom: 12 }}>
      <div
        style={{
          padding: '14px 16px 8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <SectionLabel>
          {t(`tcn.m2.bucket.${kind}`, KIND_LABELS[kind] || kind)}
        </SectionLabel>
        <Chip color={accent} size="sm" solid>
          {creators.length}
        </Chip>
      </div>
      {creators.map((c) => {
        const isExpanded = expandedId === c.creator_id;
        return (
          <React.Fragment key={c.creator_id}>
            <CreatorOnboardingRow
              creator={c}
              expanded={isExpanded}
              onClick={() => onExpand?.(isExpanded ? null : c.creator_id)}
            />
            {isExpanded && (
              <div
                style={{
                  background: TCN.surface2,
                  padding: '14px 16px 16px',
                  borderBottom: `1px solid ${TCN.border}`,
                }}
              >
                <CreatorDetailSheet
                  creator={c}
                  onCreateActionPlan={onCreateActionPlan}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </Card>
  );
};

export default CreatorBucket;
